// src/utils/addendaXml.js
// Generacion del XML de la Addenda de factura fletera para Cryoinfra.
// La addenda debe integrarse como nodo cfdi:Addenda (namespace CFDI 4.0) hijo del
// Comprobante. Extraido de AdendaBitacora.js para poder probarlo de forma aislada.

export const CFDI_NAMESPACE = 'http://www.sat.gob.mx/cfd/4';
export const XSI_NAMESPACE = 'http://www.w3.org/2001/XMLSchema-instance';
export const ADDENDA_SCHEMA_LOCATION =
  'https://servicios.cryoinfra.com.mx/wskp_sdm5/Proxy/CryoDAC-SDMSR/https://srvcorppiisn.cryoinfra.com.mx/sftf/Schemas/AddendaFacturaFletera.xsd';

// Extrae la clave de la bitacora de forma robusta desde string, numero u objeto.
export const extraerClaveBitacora = (claveBitacoraObj) => {
  // Si es null o undefined
  if (claveBitacoraObj === null || claveBitacoraObj === undefined) {
    return 'SIN_CLAVE';
  }

  // Si es un string o numero, usarlo directamente (caso de texto libre del usuario)
  if (typeof claveBitacoraObj === 'string' || typeof claveBitacoraObj === 'number') {
    const resultado = claveBitacoraObj.toString().trim();
    if (resultado === '') {
      return 'SIN_CLAVE';
    }
    return resultado;
  }

  // Si es un objeto, intentar extraer la clave
  if (typeof claveBitacoraObj === 'object') {
    // Primero intentar usar claveBitacora si existe (incluyendo 0 como valor valido)
    const clave = claveBitacoraObj.claveBitacora;
    if (clave !== null && clave !== undefined && clave !== '') {
      if (typeof clave === 'number' || (typeof clave === 'string' && clave.trim() !== '')) {
        return clave.toString().trim();
      }
    }

    // Si no tiene claveBitacora valida, intentar construir con serieBitacora + folioBitacora
    const serie = claveBitacoraObj.serieBitacora || '';
    const folio = claveBitacoraObj.folioBitacora || '';
    const serieFolio = (serie + folio).trim();

    if (serieFolio !== '') {
      return serieFolio;
    }
  }

  return 'SIN_CLAVE';
};

// Escapa caracteres especiales en valores de atributos XML (para la ruta por strings).
export const escapeXmlAttribute = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const claveConcepto = (concepto) => concepto.clave?.CodigoGasto || concepto.clave || '';

const fechaDetalle = (detalle) => `${detalle.fecha}T${new Date().toTimeString().split(' ')[0]}`;

// Integra la addenda dentro del XML de la factura y devuelve el XML resultante.
// Lanza un Error si el XML de factura es invalido o no contiene un Comprobante:
// preferimos fallar de forma visible antes que enviar una addenda mal formada.
export const integrarAddendaEnFactura = (xmlFactura, conceptos) => {
  if (!xmlFactura || typeof xmlFactura !== 'string' || xmlFactura.trim() === '') {
    throw new Error('El XML de factura esta vacio o invalido');
  }

  const xmlDoc = new DOMParser().parseFromString(xmlFactura, 'text/xml');

  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error('Error al parsear el XML de factura: ' + (parserError.textContent || 'Error desconocido'));
  }
  if (!xmlDoc.documentElement) {
    throw new Error('El XML de factura no tiene un elemento raiz valido');
  }

  const comprobante = xmlDoc.querySelector('cfdi\\:Comprobante, Comprobante');
  if (!comprobante) {
    throw new Error('El XML de factura no contiene un nodo Comprobante');
  }

  // Eliminar cualquier Addenda previa sin importar el prefijo con que quedara escrita
  // (una version anterior con bug pudo dejar <cartaporte31:Addenda> como residuo).
  Array.from(xmlDoc.getElementsByTagName('*'))
    .filter((el) => el.localName === 'Addenda')
    .forEach((el) => el.remove());

  // El namespace del propio Comprobante ES el namespace CFDI. Derivarlo del elemento
  // (no de un escaneo de atributos xmlns) evita elegir por error el de CartaPorte.
  const cfdiNamespace = comprobante.namespaceURI || CFDI_NAMESPACE;
  const addendaElement = xmlDoc.createElementNS(cfdiNamespace, 'cfdi:Addenda');
  addendaElement.setAttributeNS(XSI_NAMESPACE, 'xsi:schemaLocation', ADDENDA_SCHEMA_LOCATION);

  conceptos.forEach((concepto) => {
    const conceptoElement = xmlDoc.createElement('Concepto');
    conceptoElement.setAttribute('Clave', claveConcepto(concepto));
    conceptoElement.setAttribute('Importe', concepto.importe.toString());

    const bitacorasViajeElement = xmlDoc.createElement('BitacorasViaje');

    concepto.bitacorasViaje.forEach((bitacora) => {
      const bitacoraElement = xmlDoc.createElement('BitacoraViaje');
      bitacoraElement.setAttribute('ClaveBitacora', extraerClaveBitacora(bitacora.claveBitacora));
      bitacoraElement.setAttribute('Fecha', bitacora.fecha);
      bitacoraElement.setAttribute('Importe', bitacora.importe.toString());

      bitacora.detalles.forEach((detalle) => {
        const detalleElement = xmlDoc.createElement('Detalle');
        detalleElement.setAttribute('Descripcion', detalle.descripcion);
        detalleElement.setAttribute('Fecha', fechaDetalle(detalle));
        detalleElement.setAttribute('Importe', detalle.importe.toString());
        bitacoraElement.appendChild(detalleElement);
      });

      bitacorasViajeElement.appendChild(bitacoraElement);
    });

    conceptoElement.appendChild(bitacorasViajeElement);
    addendaElement.appendChild(conceptoElement);
  });

  comprobante.appendChild(addendaElement);

  return new XMLSerializer().serializeToString(xmlDoc);
};

// Genera una addenda suelta (cuando no hay factura). Declara el prefijo cfdi y el
// namespace xsi correctamente para que el XML sea namespace-well-formed.
export const generarAddendaStandalone = (conceptos) => {
  let xml = `<?xml version="1.0" encoding="UTF-8"?>
<cfdi:Addenda xmlns:cfdi="${CFDI_NAMESPACE}" xmlns:xsi="${XSI_NAMESPACE}" xsi:schemaLocation="${ADDENDA_SCHEMA_LOCATION}">`;

  conceptos.forEach((concepto) => {
    const conceptoClave = escapeXmlAttribute(claveConcepto(concepto));
    const conceptoImporte = escapeXmlAttribute(concepto.importe);

    xml += `
<Concepto Clave="${conceptoClave}" Importe="${conceptoImporte}">
<BitacorasViaje>`;

    concepto.bitacorasViaje.forEach((bitacora) => {
      const claveBitacora = escapeXmlAttribute(extraerClaveBitacora(bitacora.claveBitacora));
      const bitacoraFecha = escapeXmlAttribute(bitacora.fecha);
      const bitacoraImporte = escapeXmlAttribute(bitacora.importe);

      xml += `
    <BitacoraViaje ClaveBitacora="${claveBitacora}" Fecha="${bitacoraFecha}" Importe="${bitacoraImporte}">`;

      bitacora.detalles.forEach((detalle) => {
        const detalleDescripcion = escapeXmlAttribute(detalle.descripcion);
        const detalleFecha = escapeXmlAttribute(fechaDetalle(detalle));
        const detalleImporte = escapeXmlAttribute(detalle.importe);

        xml += `
<Detalle Descripcion="${detalleDescripcion}" Fecha="${detalleFecha}" Importe="${detalleImporte}" />`;
      });

      xml += `
</BitacoraViaje>`;
    });

    xml += `
</BitacorasViaje>
</Concepto>`;
  });

  xml += `
</cfdi:Addenda>`;

  return xml;
};
